import { type AnalyticsContextProviders } from './types';

/**
 * The context registry — replaces the `analyticsScribe` model's Redux state
 * (`currentTeamId`; the consumer-less `segmentContext` died with no
 * replacement) and carries the app-owned value suppliers (design D3).
 */

type MutableContext = {
  // The active team for the current workspace, set by setActiveTeam. Always the
  // team the user is in for the active workspace (or undefined when they have no
  // team there), so consumers read it directly — no per-event workspace guard.
  teamId: string | undefined;
};

const context: MutableContext = {
  teamId: undefined,
};

let providers: AnalyticsContextProviders = {};

/** Set by app wiring where `setActiveTeam` fires today. */
export function setAnalyticsContext(partial: Partial<MutableContext>): void {
  Object.assign(context, partial);
}

/**
 * Drop the active team from the registry. Called on logout and whenever the
 * active workspace changes without a full reload (e.g. accepting an invite into
 * another workspace), so the next event never carries a team scoped to the
 * workspace we just left.
 */
export function clearActiveTeam(): void {
  context.teamId = undefined;
}

/** Registered once from app wiring (module scope, alongside adapter registration). */
export function registerAnalyticsContextProviders(next: AnalyticsContextProviders): void {
  providers = { ...providers, ...next };
}

export function getAnalyticsContext(): Readonly<MutableContext> {
  return context;
}

export function getContextProviders(): Readonly<AnalyticsContextProviders> {
  return providers;
}

/** Test-only: reset all registries between tests. */
export function __resetAnalyticsContextForTests(): void {
  context.teamId = undefined;
  providers = {};
}
